import { inject, Injectable } from '@angular/core';
import { assertNever } from 'assert-never';
import { map, Observable, Subject, take } from 'rxjs';

import { GroupStoreService } from '@shure/cloud/device-management/shared/services';
import { CloudRoom, Group, InventoryDevice } from '@shure/cloud/shared/models/devices';
import { HealthSeverity } from '@shure/shared/angular/data-access/system-api/models';

import { CloudRoomsApiService } from '../api/cloud-rooms-api.service';
import { GroupsApiService } from '../api/groups-api.service';
import { InventoryDevicesApiService } from '../api/inventory-devices-api.service';

export interface InventoryDataAddedEvent<TData> {
	type: 'added';
	data: TData;
}

export interface InventoryDataUpdatedEvent<TData> {
	type: 'updated';
	data: TData;
}

export interface InventoryDataRemovedEvent {
	type: 'removed';
	id: string;
}

export type InventoryDataEvent<TData> =
	InventoryDataAddedEvent<TData> | InventoryDataUpdatedEvent<TData> | InventoryDataRemovedEvent;

/**
 * Service responsible for orchestrating the loading and management of inventory data, including devices, groups, and cloud rooms.
 * Provides a higher-level of abstraction for broadcasting events to the view layer and ensuring data consistency.
 * TODO (bisem): Move DeviceRelationshipService management from the inventory API service to this service
 */
@Injectable({ providedIn: 'root' })
export class InventoryDataAggregationService {
	private readonly groupsApiService = inject(GroupsApiService);
	private readonly cloudRoomsApiService = inject(CloudRoomsApiService);
	private readonly inventoryDevicesApiService = inject(InventoryDevicesApiService);
	private readonly groupStoreService = inject(GroupStoreService);

	private readonly _deviceEvents = new Subject<InventoryDataEvent<InventoryDevice>>();
	public readonly deviceEvents$ = this._deviceEvents.asObservable();

	private readonly _groupEvents = new Subject<InventoryDataEvent<Group>>();
	public readonly groupEvents$ = this._groupEvents.asObservable();

	private readonly _cloudRoomEvents = new Subject<InventoryDataEvent<CloudRoom>>();
	public readonly cloudRoomEvents$ = this._cloudRoomEvents.asObservable();

	constructor() {
		this.subscribeToDeviceEvents();
		this.subscribeToGroupEvents();
		this.subscribeToCloudRoomEvents();
		this.mockCloudRooms();
	}

	public getCachedDevices$(): Observable<InventoryDevice[]> {
		return this.inventoryDevicesApiService.getInventoryDevices$().pipe(take(1));
	}

	public getCachedGroups$(): Observable<Group[]> {
		return this.groupsApiService.getCachedGroups$();
	}

	public getCachedRooms$(): Observable<CloudRoom[]> {
		return this.cloudRoomsApiService.getCachedCloudRooms$().pipe(map((rooms) => [...rooms, ...this.mockRooms]));
	}

	private subscribeToDeviceEvents(): void {
		this.inventoryDevicesApiService.getInventoryDeviceEvents$().subscribe((event) => {
			switch (event.type) {
				case 'added':
					this.updateDeviceGroup(event.device);
					this._deviceEvents.next({ type: 'added', data: event.device });
					break;
				case 'updated':
					this.updateDeviceGroup(event.device);
					this._deviceEvents.next({ type: 'updated', data: event.device });
					break;
				case 'removed':
					this.groupStoreService.removeDevice(event.device.id);
					this._deviceEvents.next({ type: 'removed', id: event.device.id });
					break;
				default:
					assertNever(event.type);
			}
		});
	}

	private updateDeviceGroup(device: InventoryDevice): void {
		const groupId = device.features.group?.group?.id;
		if (groupId) {
			this.groupStoreService.setDeviceGroup(device.id, groupId);
		} else {
			this.groupStoreService.removeDevice(device.id);
		}
	}

	private subscribeToGroupEvents(): void {
		this.groupsApiService.groupAdded$.subscribe((group) => {
			this.groupStoreService.addGroup(group);
			this._groupEvents.next({ type: 'added', data: group });
		});

		this.groupsApiService.groupRemoved$.subscribe((groupId) => {
			this.groupStoreService.removeGroup(groupId);
			this._groupEvents.next({ type: 'removed', id: groupId });
		});

		this.groupsApiService.groupSnapshot$.subscribe((updatedGroup) => {
			let existingGroup;

			try {
				existingGroup = this.groupStoreService.getGroup(updatedGroup.id);
			} catch {
				this.groupStoreService.addGroup(updatedGroup);
				this._groupEvents.next({ type: 'added', data: updatedGroup });
				return;
			}

			if (existingGroup.name !== updatedGroup.name) {
				this.groupStoreService.renameGroup(updatedGroup.id, updatedGroup.name);
				this._groupEvents.next({ type: 'updated', data: updatedGroup });
			}

			if (existingGroup.parentId !== updatedGroup.parentId) {
				this.groupStoreService.moveGroup(updatedGroup.id, updatedGroup.parentId);
				this._groupEvents.next({ type: 'updated', data: updatedGroup });
			}
		});

		this.groupsApiService.suspended$.subscribe(() => {
			this.groupStoreService.clear();
		});
	}

	private subscribeToCloudRoomEvents(): void {
		this.cloudRoomsApiService.cloudRoomAdded$.subscribe((cloudRoom) => {
			this.groupStoreService.addOrUpdateCloudRoom(cloudRoom);
			this._cloudRoomEvents.next({ type: 'added', data: cloudRoom });
		});

		this.cloudRoomsApiService.cloudRoomRemoved$.subscribe((cloudRoomId) => {
			this.groupStoreService.removeCloudRoom(cloudRoomId);
			this._cloudRoomEvents.next({ type: 'removed', id: cloudRoomId });
		});

		this.cloudRoomsApiService.cloudRoomSnapshot$.subscribe((cloudRoom) => {
			this.groupStoreService.addOrUpdateCloudRoom(cloudRoom);
			this._cloudRoomEvents.next({ type: 'updated', data: cloudRoom });
		});
	}

	// TODO (carlsos): remove mock rooms once real health data is available for testing
	private readonly mockRooms: CloudRoom[] = [
		{
			id: 'mock-room-critical',
			name: 'Mock Room (Critical)',
			creationDate: new Date(),
			health: { worstSeverity: HealthSeverity.Critical }
		},
		{
			id: 'mock-room-warning',
			name: 'Mock Room (Warning)',
			creationDate: new Date(),
			health: { worstSeverity: HealthSeverity.Warning }
		}
	];

	private mockCloudRooms(): void {
		for (const room of this.mockRooms) {
			this.groupStoreService.addOrUpdateCloudRoom(room);
		}
	}
}
